import React from 'react';
import { useTranslation } from 'react-i18next';
import { Brain, ChevronDown } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import type { DesignChange, FailureMode } from '../../../types/drbfm';

interface Props {
  failureMode: FailureMode;
  designChange?: DesignChange;
}

type EvidenceLevel = 'direct' | 'supported' | 'assumption';

const DecisionBasisCard: React.FC<Props> = ({ failureMode, designChange }) => {
  const { t } = useTranslation();
  const evidenceCount = new Set(
    failureMode.knowledgeReferences
      .map((reference) => reference.documentId)
      .filter(Boolean),
  ).size;
  const causalClaims = (failureMode.causalClaims || [])
    .filter((claim) => claim.claim.trim())
    .map((claim) => ({
      ...claim,
      evidenceLevel: ['direct', 'supported', 'assumption'].includes(claim.evidenceLevel)
        ? claim.evidenceLevel
        : 'assumption' as EvidenceLevel,
    }));
  const evidenceLevelLabel = (level: EvidenceLevel) => ({
    direct: t('drbfm.decisionMemory.evidenceLevel.direct', '直接根拠'),
    supported: t('drbfm.decisionMemory.evidenceLevel.supported', '補強根拠'),
    assumption: t('drbfm.decisionMemory.evidenceLevel.assumption', '仮説'),
  }[level]);

  return (
    <Alert variant="info" className="py-3">
      <Brain className="size-4" strokeWidth={1.9} />
      <AlertTitle className="revy-cjk">
        {t('drbfm.decisionMemory.decisionBasisTitle', 'AI案の理由と根拠')}
      </AlertTitle>
      <AlertDescription className="flex flex-col gap-3">
        <div className="rounded-md border bg-background/70 p-3">
          <p className="revy-cjk text-xs font-medium text-muted-foreground">
            {t('drbfm.decisionMemory.originatingChange', '起点となった変更点')}
          </p>
          <p className="revy-cjk mt-1 text-sm leading-[1.6]">
            {designChange?.changeDescription
              || t('drbfm.decisionMemory.changeDetailsUnavailable', '変更点の詳細は未取得です')}
          </p>
        </div>

        <div className="flex flex-col items-center gap-1">
          <p className="revy-cjk w-full text-xs font-medium text-muted-foreground">
            {t('drbfm.decisionMemory.causalHypothesis', 'AIが組み立てた因果仮説')}
          </p>
          <p className="revy-cjk w-full text-sm leading-[1.6]">
            <span className="font-medium">{t('drbfm.decisionMemory.cause', '原因仮説')}: </span>
            {failureMode.failureCause || '-'}
          </p>
          <ChevronDown className="size-4 text-muted-foreground" aria-hidden="true" />
          <p className="revy-cjk w-full text-sm leading-[1.6]">
            <span className="font-medium">{t('drbfm.decisionMemory.failureMode', '故障モード候補')}: </span>
            {failureMode.failureDescription}
          </p>
          <ChevronDown className="size-4 text-muted-foreground" aria-hidden="true" />
          <p className="revy-cjk w-full text-sm leading-[1.6]">
            <span className="font-medium">{t('drbfm.decisionMemory.effect', '想定影響')}: </span>
            {failureMode.failureEffect || '-'}
          </p>
        </div>

        {causalClaims.length > 0 ? (
          <div>
            <p className="revy-cjk mb-2 text-xs font-medium text-muted-foreground">
              {t('drbfm.decisionMemory.recordedEvidence', '記録された因果根拠')}
            </p>
            <ul className="flex flex-col gap-2">
              {causalClaims.map((claim, index) => (
                <li
                  key={`${claim.claim}-${index}`}
                  className="revy-cjk flex items-start gap-2 text-sm leading-[1.6]"
                >
                  <Badge
                    variant={claim.evidenceLevel === 'direct'
                      ? 'success'
                      : claim.evidenceLevel === 'supported' ? 'info' : 'warning'}
                    className="mt-0.5 shrink-0"
                  >
                    {evidenceLevelLabel(claim.evidenceLevel)}
                  </Badge>
                  <span>
                    {claim.claim}
                    {claim.evidence && ` — ${claim.evidence}`}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <p className="revy-cjk rounded-md border border-dashed p-3 text-sm leading-[1.6] text-muted-foreground">
            {t(
              'drbfm.decisionMemory.evidenceNotGenerated',
              'この候補を裏づける因果根拠はまだ記録されていません。因果関係は設計者が確認してください。',
            )}
          </p>
        )}

        <p className="revy-cjk text-xs leading-[1.6] text-muted-foreground">
          {t('drbfm.decisionMemory.evidenceBoundary', {
            count: evidenceCount,
            defaultValue: `関連資料 ${evidenceCount}件を参照したAI候補です。参照数は因果関係の裏づけを保証しません。妥当性は設計者が確認してください。`,
          })}
        </p>
      </AlertDescription>
    </Alert>
  );
};

export default DecisionBasisCard;
